import React, { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Clock3,
  GripVertical,
  PoundSterling,
  Plus,
  Trash2,
  Users,
  Loader2,
  AlertCircle,
  Sparkles,
  TrendingDown,
  Scale,
  Shield,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { key: "Mon", label: "Mon" },
  { key: "Tue", label: "Tue" },
  { key: "Wed", label: "Wed" },
  { key: "Thu", label: "Thu" },
  { key: "Fri", label: "Fri" },
  { key: "Sat", label: "Sat" },
  { key: "Sun", label: "Sun" },
];

const LENGTH_OPTIONS = [2, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 11, 12];

function formatMoney(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(value || 0);
}

function formatHours(value) {
  return `${value}h`;
}

function getTimeLabel(startTime, endTime) {
  return `${startTime} – ${endTime}`;
}

function calcHours(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);
  return Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
}

function endTimeFromStart(startHour, lengthHours) {
  const totalMinutes = Number(startHour) * 60 + Number(lengthHours) * 60;
  const endH = Math.min(Math.floor(totalMinutes / 60), 23);
  const endM = totalMinutes % 60;
  return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
}

export default function RotaBuilder({ locationId, sessionId, employees: dashEmployees }) {
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedEmployeeId, setDraggedEmployeeId] = useState(null);
  const [selectedDay, setSelectedDay] = useState("Mon");
  const [startHour, setStartHour] = useState("09");
  const [lengthHours, setLengthHours] = useState("8");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [savingShift, setSavingShift] = useState(false);
  const [showAutoFill, setShowAutoFill] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  const autoFillRota = async (strategy) => {
    setAutoFilling(true);
    setShowAutoFill(false);
    try {
      const res = await axios.post(
        `${API}/manager/rota/auto-fill/${locationId}?session_id=${sessionId}`,
        { strategy }
      );
      if (res.data.success) {
        // Reload the rota data to get the new shifts
        await loadRotaData();
        // No notification as per user request - the UI update is feedback enough
      }
    } catch (err) {
      console.error("Auto-fill error:", err);
      // Only show error if something actually went wrong
      if (err.response?.status !== 200) {
        toast.error(err.response?.data?.detail || "Failed to auto-fill schedule");
      }
    } finally {
      setAutoFilling(false);
    }
  };

  // Load rota data
  const loadRotaData = useCallback(async () => {
    if (!locationId || !sessionId) return;
    setLoading(true);
    try {
      const [empRes, schedRes] = await Promise.all([
        axios.get(`${API}/manager/rota/employees/${locationId}?session_id=${sessionId}`),
        axios.get(`${API}/manager/rota/schedule/${locationId}?session_id=${sessionId}`),
      ]);

      const rotaEmployees = (empRes.data.employees || []).map((e) => ({
        id: e.id,
        name: e.name || "Unknown",
        role: e.role || "Staff",
        rate: e.hourly_rate || 0,
      }));

      setEmployees(rotaEmployees);
      if (rotaEmployees.length > 0 && !selectedEmployee) {
        setSelectedEmployee(rotaEmployees[0].id);
      }

      // Group shifts by day from schedule response
      const scheduleData = schedRes.data.schedule || {};
      const grouped = {};
      DAYS.forEach((d) => (grouped[d.key] = []));
      Object.entries(scheduleData).forEach(([day, dayShifts]) => {
        if (grouped[day]) {
          grouped[day] = (dayShifts || []).map((s) => ({
            id: s.id,
            employeeId: s.empId,
            startTime: s.start,
            endTime: s.end,
          }));
        }
      });
      setShifts(grouped);
    } catch (err) {
      console.error("Error loading rota:", err);
      // Silently fail - rota data may not be available
    } finally {
      setLoading(false);
    }
  }, [locationId, sessionId]);

  useEffect(() => {
    loadRotaData();
  }, [loadRotaData]);

  // Close auto-fill panel on click outside
  useEffect(() => {
    if (!showAutoFill) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="rota-auto-fill-panel"]') && !e.target.closest('[data-testid="rota-auto-fill-btn"]')) {
        setShowAutoFill(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showAutoFill]);

  const addShift = async (dayKey, employeeId, start = startHour, length = lengthHours) => {
    const emp = employees.find((e) => e.id === employeeId);
    if (!emp) return;

    const startTime = `${String(start).padStart(2, "0")}:00`;
    const endTime = endTimeFromStart(start, length);

    setSavingShift(true);
    try {
      const res = await axios.post(
        `${API}/manager/rota/schedule/${locationId}/shift?session_id=${sessionId}`,
        { day: dayKey, employee_id: employeeId, start_time: startTime, end_time: endTime }
      );
      if (res.data.success || res.data.shift_id) {
        const newShift = {
          id: res.data.shift_id || res.data.id || `temp-${Date.now()}`,
          employeeId,
          startTime,
          endTime,
        };
        setShifts((prev) => ({
          ...prev,
          [dayKey]: [...(prev[dayKey] || []), newShift].sort((a, b) => a.startTime.localeCompare(b.startTime)),
        }));
        toast.success(`Shift added for ${emp.name}`);
      }
    } catch (err) {
      console.error("Error adding shift:", err);
      toast.error(err.response?.data?.detail || "Failed to add shift");
    } finally {
      setSavingShift(false);
    }
  };

  const removeShift = async (dayKey, shiftId) => {
    try {
      await axios.delete(`${API}/manager/rota/schedule/${locationId}/shift/${shiftId}?session_id=${sessionId}`);
      setShifts((prev) => ({
        ...prev,
        [dayKey]: prev[dayKey].filter((s) => s.id !== shiftId),
      }));
      toast.success("Shift removed");
    } catch (err) {
      console.error("Error removing shift:", err);
      toast.error("Failed to remove shift");
    }
  };

  const updateRate = async (employeeId, rate) => {
    setEmployees((prev) => prev.map((e) => (e.id === employeeId ? { ...e, rate: Number(rate) || 0 } : e)));
    try {
      await axios.put(
        `${API}/manager/rota/employee/${employeeId}?session_id=${sessionId}`,
        { hourly_rate: Number(rate) || 0 }
      );
    } catch (err) {
      console.error("Error updating rate:", err);
    }
  };

  const totals = useMemo(() => {
    const dayTotals = {};
    let weeklyHours = 0;
    let weeklyCost = 0;

    DAYS.forEach((day) => {
      const list = shifts[day.key] || [];
      const dayHours = list.reduce((sum, s) => sum + calcHours(s.startTime, s.endTime), 0);
      const dayCost = list.reduce((sum, s) => {
        const emp = employees.find((e) => e.id === s.employeeId);
        return sum + calcHours(s.startTime, s.endTime) * (emp?.rate || 0);
      }, 0);
      dayTotals[day.key] = { hours: dayHours, cost: dayCost };
      weeklyHours += dayHours;
      weeklyCost += dayCost;
    });

    const employeeTotals = employees.map((emp) => {
      const assigned = DAYS.flatMap((d) => (shifts[d.key] || []).filter((s) => s.employeeId === emp.id));
      const hours = assigned.reduce((sum, s) => sum + calcHours(s.startTime, s.endTime), 0);
      return { ...emp, hours, cost: hours * emp.rate };
    });

    return { dayTotals, weeklyHours, weeklyCost, employeeTotals };
  }, [employees, shifts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex flex-col gap-4 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-violet-500/15 px-3 py-1 text-sm font-medium text-violet-300 ring-1 ring-violet-400/20">
            <CalendarDays className="h-4 w-4" />
            Rota Builder
          </div>
          <h2 className="text-2xl font-semibold text-white">Weekly Scheduling</h2>
          <p className="mt-1 text-sm text-zinc-400">
            Drag employees into a day, set shift lengths, and see total hours and wage cost.
          </p>
          <div className="mt-3 relative">
            <Button
              onClick={() => setShowAutoFill(!showAutoFill)}
              disabled={autoFilling || employees.length === 0}
              className="rounded-xl bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 ring-1 ring-emerald-400/25 transition-all"
              data-testid="rota-auto-fill-btn"
            >
              {autoFilling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              {autoFilling ? "Generating..." : "Auto-Fill Schedule"}
            </Button>
            <AnimatePresence>
              {showAutoFill && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 top-full mt-2 z-50 w-80 rounded-xl border border-zinc-700 bg-zinc-900 p-4 shadow-2xl"
                  data-testid="rota-auto-fill-panel"
                >
                  <p className="text-sm font-semibold text-white mb-1">Choose optimisation strategy</p>
                  <p className="text-xs text-zinc-500 mb-3">This will replace all existing shifts.</p>
                  <div className="space-y-2">
                    <button
                      onClick={() => autoFillRota("minimize_cost")}
                      className="w-full flex items-start gap-3 rounded-lg border border-zinc-700 bg-zinc-800/60 p-3 text-left hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors group"
                      data-testid="auto-fill-minimize-cost"
                    >
                      <TrendingDown className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white group-hover:text-emerald-200">Minimise Cost</p>
                        <p className="text-xs text-zinc-500">Favour lower-paid staff first to reduce wage bill</p>
                      </div>
                    </button>
                    <button
                      onClick={() => autoFillRota("fair_distribution")}
                      className="w-full flex items-start gap-3 rounded-lg border border-zinc-700 bg-zinc-800/60 p-3 text-left hover:border-violet-500/40 hover:bg-violet-500/5 transition-colors group"
                      data-testid="auto-fill-fair-distribution"
                    >
                      <Scale className="h-5 w-5 text-violet-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white group-hover:text-violet-200">Fair Distribution</p>
                        <p className="text-xs text-zinc-500">Spread hours evenly across all staff</p>
                      </div>
                    </button>
                    <button
                      onClick={() => autoFillRota("coverage")}
                      className="w-full flex items-start gap-3 rounded-lg border border-zinc-700 bg-zinc-800/60 p-3 text-left hover:border-sky-500/40 hover:bg-sky-500/5 transition-colors group"
                      data-testid="auto-fill-coverage"
                    >
                      <Shield className="h-5 w-5 text-sky-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-white group-hover:text-sky-200">Ensure Coverage</p>
                        <p className="text-xs text-zinc-500">Fill shifts based on availability, no cost weighting</p>
                      </div>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Weekly hours" value={formatHours(totals.weeklyHours)} icon={<Clock3 className="h-4 w-4" />} />
          <StatCard label="Wage cost" value={formatMoney(totals.weeklyCost)} icon={<PoundSterling className="h-4 w-4" />} />
          <StatCard label="Staff" value={`${employees.length}`} icon={<Users className="h-4 w-4" />} />
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[280px_1fr_280px]">
        {/* Left sidebar: Employees */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 space-y-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
            <Users className="h-5 w-5 text-violet-400" /> Employees
          </h3>

          {loading && employees.length === 0 && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-zinc-600 mx-auto mb-2 animate-spin" />
              <p className="text-sm text-zinc-500">Loading employees...</p>
            </div>
          )}

          {!loading && employees.length === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No employees found. Add employees from the Employees tab first.</p>
            </div>
          )}

          {employees.map((employee) => {
            const total = totals.employeeTotals.find((e) => e.id === employee.id);
            return (
              <motion.div
                key={employee.id}
                layout
                draggable
                onDragStart={() => setDraggedEmployeeId(employee.id)}
                onDragEnd={() => setDraggedEmployeeId(null)}
                whileDrag={{ scale: 1.02 }}
                className="rounded-xl border border-zinc-700 bg-zinc-800/60 p-4 cursor-grab active:cursor-grabbing hover:border-zinc-600 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                      <p className="truncate font-medium text-white text-sm">{employee.name}</p>
                    </div>
                    <p className="mt-0.5 text-xs text-zinc-500 ml-6">{employee.role}</p>
                  </div>
                  <span className="text-xs bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full ring-1 ring-emerald-400/20 whitespace-nowrap">
                    {formatMoney(employee.rate)}/hr
                  </span>
                </div>

                <div className="mt-3">
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Hourly pay</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={employee.rate}
                    onChange={(e) => updateRate(employee.id, e.target.value)}
                    className="mt-1 h-9 rounded-lg bg-zinc-900 border-zinc-700 text-white text-sm [color-scheme:dark]"
                    data-testid={`rota-rate-${employee.id}`}
                  />
                </div>

                <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
                  <span>{formatHours(total?.hours || 0)} assigned</span>
                  <span>{formatMoney(total?.cost || 0)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Center: Quick add + Day cards */}
        <div className="space-y-4">
          {/* Quick add */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5">
            <h3 className="text-base font-semibold text-white mb-3">Quick Add</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="rounded-lg bg-zinc-800 border-zinc-700 text-white" data-testid="rota-select-employee">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedDay} onValueChange={setSelectedDay}>
                <SelectTrigger className="rounded-lg bg-zinc-800 border-zinc-700 text-white" data-testid="rota-select-day">
                  <SelectValue placeholder="Day" />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => (
                    <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={startHour} onValueChange={setStartHour}>
                <SelectTrigger className="rounded-lg bg-zinc-800 border-zinc-700 text-white" data-testid="rota-select-start">
                  <SelectValue placeholder="Start" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 33 }, (_, i) => {
                    const h = Math.floor(i / 2) + 5;
                    const m = i % 2 === 0 ? "00" : "30";
                    if (h > 21) return null;
                    const val = `${h}${m === "30" ? ".5" : ""}`;
                    return (
                      <SelectItem key={`${h}:${m}`} value={val}>
                        {String(h).padStart(2, "0")}:{m}
                      </SelectItem>
                    );
                  }).filter(Boolean)}
                </SelectContent>
              </Select>

              <Select value={lengthHours} onValueChange={setLengthHours}>
                <SelectTrigger className="rounded-lg bg-zinc-800 border-zinc-700 text-white" data-testid="rota-select-length">
                  <SelectValue placeholder="Length" />
                </SelectTrigger>
                <SelectContent>
                  {LENGTH_OPTIONS.map((h) => (
                    <SelectItem key={h} value={String(h)}>{h % 1 === 0 ? `${h} hours` : `${h} hours`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => addShift(selectedDay, selectedEmployee)}
              disabled={savingShift || !selectedEmployee}
              className="mt-3 rounded-xl bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 ring-1 ring-violet-400/25"
              data-testid="rota-add-shift-btn"
            >
              {savingShift ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Add shift
            </Button>
          </div>

          {/* Day cards grid */}
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {DAYS.map((day) => (
              <div
                key={day.key}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (draggedEmployeeId) addShift(day.key, draggedEmployeeId);
                }}
                className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h4 className="text-base font-semibold text-white">{day.label}</h4>
                    <p className="text-xs text-zinc-500">
                      {formatHours(totals.dayTotals[day.key]?.hours || 0)} &middot; {formatMoney(totals.dayTotals[day.key]?.cost || 0)}
                    </p>
                  </div>
                  <span className="text-xs bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded-full ring-1 ring-zinc-700">
                    Drop here
                  </span>
                </div>
                <div className="space-y-2 min-h-[180px]">
                  {(shifts[day.key] || []).length === 0 ? (
                    <div className="flex h-[150px] items-center justify-center rounded-xl border border-dashed border-zinc-700 bg-zinc-800/30 text-sm text-zinc-600">
                      Drag an employee here
                    </div>
                  ) : (
                    shifts[day.key].map((shift) => {
                      const emp = employees.find((e) => e.id === shift.employeeId);
                      const hrs = calcHours(shift.startTime, shift.endTime);
                      const cost = hrs * (emp?.rate || 0);
                      return (
                        <motion.div key={shift.id} layout className="rounded-xl border border-zinc-700 bg-zinc-800/60 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-medium text-white text-sm">{emp?.name || "Unknown"}</p>
                              <p className="text-xs text-zinc-500">{emp?.role}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 rounded-lg text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10"
                              onClick={() => removeShift(day.key, shift.id)}
                              data-testid={`rota-remove-shift-${shift.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className="text-xs bg-violet-500/15 text-violet-300 px-2 py-0.5 rounded-full ring-1 ring-violet-400/20">
                              {getTimeLabel(shift.startTime, shift.endTime)}
                            </span>
                            <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full ring-1 ring-zinc-700">
                              {formatHours(hrs)}
                            </span>
                            <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full ring-1 ring-zinc-700">
                              {formatMoney(cost)}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right sidebar: Cost summary */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5 space-y-3">
          <h3 className="text-lg font-semibold text-white">Cost Summary</h3>
          {DAYS.map((day) => (
            <div key={day.key} className="flex items-center justify-between rounded-xl border border-zinc-700 bg-zinc-800/40 px-4 py-3 text-sm">
              <div>
                <p className="font-medium text-white">{day.label}</p>
                <p className="text-xs text-zinc-500">{formatHours(totals.dayTotals[day.key]?.hours || 0)}</p>
              </div>
              <p className="font-medium text-white">{formatMoney(totals.dayTotals[day.key]?.cost || 0)}</p>
            </div>
          ))}
          <div className="rounded-xl bg-violet-500/15 ring-1 ring-violet-400/20 p-4 text-white">
            <p className="text-sm text-violet-300/80">Estimated weekly wage cost</p>
            <p className="mt-2 text-3xl font-semibold text-violet-200">{formatMoney(totals.weeklyCost)}</p>
            <p className="mt-1 text-xs text-violet-300/60">Based on assigned hours and hourly rates.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-800/50 px-4 py-3">
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}
